import { useState } from 'react';
import { PencilIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type MutationLocus } from '../../api/genetics';
import { getApiHostUrl } from '../../config/api';

interface EditMutationLocusFormProps {
  locus: MutationLocus;
  onSaved: () => void;
  onCancel: () => void;
}

function illustrationSrc(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  const host = getApiHostUrl().replace(/\/$/, '');
  return `${host}${url.startsWith('/') ? url : `/${url}`}`;
}

export default function EditMutationLocusForm({
  locus,
  onSaved,
  onCancel,
}: EditMutationLocusFormProps) {
  const [description, setDescription] = useState(locus.description ?? '');
  const [linkageGroup, setLinkageGroup] = useState(locus.linkageGroup ?? '');
  const [recombinationPercent, setRecombinationPercent] = useState(
    locus.linkageRecombinationRate != null ? String(Math.round(locus.linkageRecombinationRate * 100)) : '',
  );
  const [illustrationUrl, setIllustrationUrl] = useState(locus.illustrationUrl);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const trimmedGroup = linkageGroup.trim();
      let linkageRecombinationRate: number | undefined;
      if (recombinationPercent.trim()) {
        const parsed = Number(recombinationPercent);
        if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
          setError('Recombination rate must be between 0 and 100.');
          setSaving(false);
          return;
        }
        linkageRecombinationRate = parsed / 100;
      }

      await geneticsApi.updateLocus(locus.id, {
        description: description.trim(),
        linkageGroup: trimmedGroup || undefined,
        linkageRecombinationRate,
      });
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update locus');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const updated = await geneticsApi.uploadLocusIllustration(locus.id, file);
      setIllustrationUrl(updated.illustrationUrl);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to upload illustration');
    } finally {
      setUploading(false);
    }
  };

  const handleClearIllustration = async () => {
    setUploading(true);
    setError(null);
    try {
      await geneticsApi.clearLocusIllustration(locus.id);
      setIllustrationUrl(undefined);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to remove illustration');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-2 rounded border border-dashed border-indigo-200 bg-indigo-50/50 p-2 space-y-2">
      <p className="text-xs font-medium text-slate-700">Edit locus</p>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Visual effect</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Dilutes eumelanin so body looks cinnamon-brown; sex-linked recessive — visual in hemizygous females and homozygous males."
          rows={3}
          className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <p className="mt-1 text-xs text-slate-500">
          Keep it short: what you see, plus how the gene acts (pigment removed/diluted, incomplete dominant, etc.).
        </p>
      </div>
      <div className="rounded border border-slate-200 bg-white p-2 space-y-2">
        <p className="text-xs font-medium text-slate-700 flex items-center gap-1">
          <PhotoIcon className="h-3.5 w-3.5" />
          Illustration
        </p>
        {illustrationUrl ? (
          <img
            src={illustrationSrc(illustrationUrl)}
            alt={`${locus.locusName} illustration`}
            className="h-20 w-20 rounded object-cover border border-slate-200"
          />
        ) : (
          <p className="text-xs text-slate-400">No illustration yet — upload a photo showing this mutation.</p>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <label className="inline-flex items-center rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer">
            {uploading ? 'Uploading…' : 'Upload photo'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              disabled={uploading}
              onChange={(e) => void handleUpload(e.target.files?.[0] ?? null)}
            />
          </label>
          {illustrationUrl ? (
            <button
              type="button"
              onClick={() => void handleClearIllustration()}
              disabled={uploading}
              className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          value={linkageGroup}
          onChange={(e) => setLinkageGroup(e.target.value)}
          placeholder="Linkage group (optional)"
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <input
          type="number"
          min={0}
          max={100}
          value={recombinationPercent}
          onChange={(e) => setRecombinationPercent(e.target.value)}
          placeholder="Recombination % (0–100)"
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
      </div>
      <p className="text-xs text-slate-500">
        Loci sharing a linkage group are modeled as linked Z-chromosome haplotypes during prediction.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          <PencilIcon className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-slate-600 hover:text-slate-800">
          Cancel
        </button>
      </div>
    </div>
  );
}
